export function formatBytes(bytes: number, decimals: number = 2, specifier = true) {
    if (bytes === 0) return specifier ? "0 Bytes" : "0";

    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ["Bytes", "KB", "MB", "GB"];

    let i = Math.floor(Math.log(bytes) / Math.log(k));
    i = i >= sizes.length ? sizes.length - 1 : i; // Ensure 'i' is within bounds

    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + (specifier ? " " + sizes[i] : "");
}

export function formatNumber(num: number | undefined): string {
    if (num == undefined) return "0";
    if (num < 1000) return num.toString();
    if (num < 1000000) return (num / 1000).toFixed(1) + "K";
    return (num / 1000000).toFixed(1) + "M";
}

export function formatDate(inputDateTime: Date, forceYear = false): string {
    const hours = inputDateTime.getHours();
    const minutes = inputDateTime.getMinutes();
    const seconds = inputDateTime.getSeconds();

    const paddedHours = String(hours).padStart(2, "0");
    const paddedMinutes = String(minutes).padStart(2, "0");
    const paddedSeconds = String(seconds).padStart(2, "0");

    const year = inputDateTime.getFullYear();
    const month = new String(inputDateTime.getMonth() + 1).padStart(2, "0");
    const day = new String(inputDateTime.getDate()).padStart(2, "0");

    const currDate = new Date();
    const printYear =
        currDate.getFullYear() != inputDateTime.getFullYear() ||
        currDate.getMonth() != inputDateTime.getMonth() ||
        currDate.getDay() != inputDateTime.getDay() ||
        forceYear;

    return paddedHours + ":" + paddedMinutes + ":" + paddedSeconds + (printYear ? ` ${year}-${month}-${day}` : "");
}

export function getYearMonthDayString(date: Date): string {
    const year = date.getFullYear();
    const month = new String(date.getMonth() + 1).padStart(2, "0");
    const day = new String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
}

export function getTimeDifference(utcTimestamp: number | Date, now: number | Date = Date.now()): string {
    const startTime = typeof utcTimestamp == "number" ? utcTimestamp : utcTimestamp.getTime();
    const endTime = typeof now == "number" ? now : now.getTime();
    const timeDifference = endTime - startTime;

    const seconds = Math.floor(timeDifference / 1000);
    if (seconds < 60) {
        return Math.max(seconds, 0) + "s";
    }

    const minutes = Math.floor(timeDifference / (1000 * 60));
    if (minutes < 60) {
        return minutes + "m";
    }

    const hours = Math.floor(timeDifference / (1000 * 60 * 60));
    if (hours < 24) {
        return hours + "h";
    }

    const days = Math.floor(timeDifference / (1000 * 60 * 60 * 24));
    if (days < 30) {
        return days + "d";
    }

    const months = Math.floor(timeDifference / (1000 * 60 * 60 * 24 * 30));
    if (months < 12) {
        return months + "mo";
    }

    const years = Math.floor(timeDifference / (1000 * 60 * 60 * 24 * 365));
    return years + "y";
}

export function assertNever(x: never) {
    throw new Error("Unexpected object: " + x);
}

export function error(message: string, exception?: any) {
    if (exception instanceof Error && exception.message.length == 0) exception = undefined;
    console.error(formatDate(new Date()) + " - " + message, exception);
    return new Error(message);
}

let debugLogId = 0;
export function debugLog(message: string, obj?: any) {
    console.log(`${(debugLogId++).toString().padStart(10, "0")} -- ${message}`);
    if (obj) console.log(obj);
}

export function sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Exponential backoff with jitter for handling rate limiting
 * @param attempt - Current attempt number (0-based)
 * @param baseDelay - Base delay in milliseconds (default: 1000)
 * @param maxDelay - Maximum delay in milliseconds (default: 30000)
 * @param jitter - Whether to add random jitter (default: true)
 * @returns Promise that resolves after the calculated delay
 */
export async function exponentialBackoff(attempt: number, baseDelay: number = 1000, maxDelay: number = 30000, jitter: boolean = true): Promise<void> {
    const delay = Math.min(baseDelay * Math.pow(2, attempt), maxDelay);
    const jitterAmount = jitter ? Math.random() * delay * 0.1 : 0;
    const totalDelay = delay + jitterAmount;

    return sleep(totalDelay);
}

/**
 * Check if an error is a rate limit error (429)
 * @param error - The error to check
 * @returns true if the error is a rate limit error
 */
export function isRateLimitError(error: any): boolean {
    return (
        error instanceof Error &&
        (error.message.includes("429") || error.message.includes("Too Many Requests") || error.message.includes("Rate limit"))
    );
}

/**
 * Retry a function with exponential backoff for 429 errors
 * @param fn - Function to retry
 * @param maxRetries - Maximum number of retries (default: 5)
 * @param baseDelay - Base delay in milliseconds (default: 1000)
 * @param maxDelay - Maximum delay in milliseconds (default: 30000)
 * @returns Promise that resolves with the function result
 */
export async function retryWithBackoff<T>(
    fn: () => Promise<T>,
    maxRetries: number = 5,
    baseDelay: number = 4000,
    maxDelay: number = 64000
): Promise<T> {
    let lastError: Error;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
        try {
            return await fn();
        } catch (error) {
            lastError = error as Error;

            // Check if it's a 429 error (rate limiting)
            if (!isRateLimitError(error) || attempt === maxRetries) {
                throw error;
            }

            console.log(
                `Rate limit hit, retrying in ${Math.min(baseDelay * Math.pow(2, attempt), maxDelay)}ms (attempt ${attempt + 1}/${maxRetries})`
            );
            await exponentialBackoff(attempt, baseDelay, maxDelay);
        }
    }

    throw lastError!;
}

export function getYear(utc: number) {
    return new Date(utc * 1000).getFullYear();
}

export function unescapeHtml(html: string): string {
    const doc = new DOMParser().parseFromString(html, "text/html");
    return doc.documentElement.textContent || "";
}

export function escapeHtml(html: string): string {
    return html.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#039;");
}

/**
 * Masks sensitive text by replacing characters with random ones while preserving structure
 * @param text - The text to mask
 * @returns Masked text with random letters/numbers, preserving spaces and special characters
 */
export function maskText(text: string): string {
    const upperChars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    const lowerChars = "abcdefghijklmnopqrstuvwxyz";
    const digits = "0123456789";

    return text
        .split("")
        .map((char) => {
            if (char >= "A" && char <= "Z") {
                return upperChars[Math.floor(Math.random() * upperChars.length)];
            } else if (char >= "a" && char <= "z") {
                return lowerChars[Math.floor(Math.random() * lowerChars.length)];
            } else if (char >= "0" && char <= "9") {
                return digits[Math.floor(Math.random() * digits.length)];
            }
            return char; // Keep spaces and special characters
        })
        .join("");
}
